/**
 * THE ONE FETCH WRAPPER. `tests/structure.test.ts` (1) asserts `fetch(` appears nowhere else under `src/`
 * except `obs/beacon.ts`.
 *
 * The rule is not tidiness. Each mechanism below is a defect this client has actually shipped, and every one
 * of them is invisible at the call site that omits it:
 *
 *   • NO TIMEOUT AND NO `AbortController`. A hung call is a UI stuck on "…" forever, and there is nothing on
 *     screen to distinguish it from a market that is simply quiet.
 *   • A `!ok` response throwing `new Error("500 /path")`, which no caller can branch on.
 *   • Money calls reaching `authHeaders()` returning `{}` and proceeding UNAUTHENTICATED rather than
 *     refusing. `auth: "required"` here throws BEFORE the request leaves.
 *
 * WHAT THIS FILE DOES NOT DO: it does not retry a POST, ever, and it does not retry a 429 on any method.
 * A POST retry is real money twice. A 429 retry triples the load under exactly the condition the bucket
 * exists for; `Retry-After` is surfaced on the `ApiError` and the controller's own refresh is the retry.
 */

import { nowMs } from "../lib/time.ts";
import { CLIENT_API_KEY, MARKET_BASE, TRADE_BASE } from "./config.ts";
import { ApiError, errorFromBody } from "./errors.ts";
import { triggerTokenFor } from "./triggerToken.ts";

export interface Req {
  base: "market" | "trade";
  /** NEVER contains a query string. Params are passed separately so they are encoded exactly once — a path
   *  with a `?` in it and a `params` object produce two `?`, and the server sees the second as part of a
   *  value. */
  path: string;
  params?: Record<string, string | number | boolean | undefined>;
  /** `DELETE` exists for exactly one route — signing out. It is a MUTATING method and therefore inherits the
   *  never-retried rule below alongside `POST`. */
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  /**
   * `"required"` — a bearer or the call never leaves. `"optional"` — attach one IF we hold one, and send the
   * request either way. `"none"` — never attach one.
   *
   * ⚠️ `"optional"` IS FOR A ROUTE THAT IS PUBLIC ON THE SERVER AND STILL WANTS THE BEARER, WHICH TODAY IS
   * `/quote` ALONE. The server reads it there only to pick a rate-limit lane: with a bearer the caller is
   * bounded per WALLET, without one it shares a per-IP lane with everyone behind the same address. So sending
   * it when we have it is not belt-and-braces — it is what stops a signed-in user being throttled by a
   * stranger on the same NAT.
   *
   * IT IS NOT A SOFTER `"required"`. Adding it to a route that moves money would send a money call with no
   * bearer and discover the mistake from the server's 401, which is exactly what `"required"` exists to
   * prevent — see the throw below. A route gets `"optional"` only when the server's own table says `public`.
   */
  auth?: "required" | "optional" | "none";
  /**
   * THE WALLET THIS REQUEST IS ABOUT, when the route reaches Jupiter Trigger.
   *
   * Set it and the client attaches that wallet's `X-Trigger-Token` if it holds one. Naming the owner rather
   * than passing the token means a caller cannot send one wallet's authorisation on another's request — the
   * lookup does the matching, in one place, instead of every call site remembering to.
   */
  triggerOwner?: string;
  timeoutMs?: number;
  /** Composed with the timeout's own controller, so a caller's cancel and the timeout both work. */
  signal?: AbortSignal;
}

/** The default budget. A read that has not answered in 10 s has failed in every way the user cares about. */
export const DEFAULT_TIMEOUT_MS = 10_000;

/** GET only, and never on a 429. */
export const MAX_GET_RETRIES = 2;
const RETRY_BASE_MS = 300;
const RETRY_CAP_MS = 1_200;

/**
 * THE SESSION PROVIDER SEAM.
 *
 * `auth: "required"` needs a bearer, and this run does not build the trader surface that mints one. Rather
 * than leave a `TODO` that resolves to "send nothing" — which IS the unauthenticated-money-call defect above,
 * restated — the provider
 * is a nullable hook that DEFAULTS TO NULL and whose absence is a hard refusal. When the trader surface
 * lands it calls `setSessionProvider` once and no call site changes.
 */
type SessionProvider = () => string | null;
let sessionProvider: SessionProvider = () => null;
/**
 * THE SIGNED-IN WALLET, for the SESSIONLESS trader surface.
 *
 * ⚠️ THE SERVER STOPPED DERIVING THE OWNER FROM THE BEARER. v2's `/user/v1` supports wallet types that
 * cannot produce the `signMessage` a sign-in needs, so every money route now names the wallet in the
 * REQUEST — `owner` in the body, `?owner=` on a GET — and a request without one is `400 {"field":"owner"}`.
 * This client was written against the session-derived shape, where `owner` was documented INERT and the
 * call sites deliberately stripped it (`buildSwap(body: Omit<SwapBuildRequest, "owner">)`), so every buy,
 * sell and withdrawal returned that 400 the moment the surface changed underneath it.
 *
 * It is injected HERE, at the one boundary that already decides "this is a trade call and it needs a
 * credential", rather than at the five call sites that need it. Five copies of "remember the owner" is how
 * the sixth route ships without it — and the owner and the bearer answer the same question ("which
 * wallet?"), so they belong in the same place.
 *
 * A body that ALREADY names an owner is left alone: `/transfer/build/external` legitimately builds for a
 * wallet that is not the signed-in one, and silently overwriting it would be the client correcting a caller
 * that meant what it said.
 */
export type OwnerProvider = () => string | null;
let ownerProvider: OwnerProvider = () => null;
export function setOwnerProvider(p: OwnerProvider): void {
  ownerProvider = p;
}

/**
 * ⚠️ THERE IS NO LIST OF ROUTES THAT NEED THE OWNER, AND THERE MUST NEVER BE ONE AGAIN.
 *
 * There was: a hand-maintained `NEEDS_OWNER` set of ten literal paths. It was wrong in three ways at once
 * when it was finally checked against the server, on 2026-09-15:
 *
 *   MISSING     /vault, /vault/challenge, /vault/register, /deposit/build, /order/create, /orders,
 *               /order/:id/cancel, /order/:id/cancel/confirm, /swap/build/gasless — nine routes, the whole
 *               trigger and DCA surface, every one of them answering `400 {"field":"owner"}`
 *   PHANTOM     /swap/build/external and /submit/external, which are not routes the server serves
 *   WRONG       /transfer/build and /withdraw/build, which do not require one
 *
 * And it could not have worked for `/order/:id/cancel` under any maintenance: a Set of literal paths cannot
 * match a path carrying a real id. The list was not stale, it was the wrong shape.
 *
 * The comment above the list predicted its own failure — "five copies of 'remember the owner' is how the
 * sixth route ships without it" — and then answered it with a sixth copy. A default that must be opted into
 * per route fails silently on the route nobody remembered. A default that is simply ON cannot.
 *
 * So: EVERY trade-base request carries the wallet. A body that already names an owner is untouched, which
 * is what keeps `/transfer/build/external` able to build for somebody else. Routes that do not need it read
 * their fields by name and ignore the extra — verified against the trader's own handlers, none of which
 * reject unknown fields.
 */

export function withOwner(r: Req): Req {
  if (r.base !== "trade") return r;
  const owner = ownerProvider();
  if (owner === null || owner === "") return r;

  if ((r.method ?? "GET") === "GET") {
    if (r.params?.owner !== undefined) return r;
    return { ...r, params: { ...(r.params ?? {}), owner } };
  }
  const body = (r.body ?? {}) as Record<string, unknown>;
  if (typeof body.owner === "string" && body.owner !== "") return r;
  return { ...r, body: { ...body, owner } };
}

export function setSessionProvider(p: SessionProvider): void {
  sessionProvider = p;
}

function withParams(url: string, params: Req["params"]): string {
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    // ABSENT, not empty. `?interval=` is a 400 on this API by design — an empty string is a client bug and
    // substituting a default silently renders the wrong grain. So an `undefined` param is OMITTED, and a
    // caller that wants the server's default passes nothing.
    if (v === undefined) continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

const jitter = (attempt: number): number => {
  const base = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** attempt);
  // ±25 %. Without jitter every client's backoff is phase-locked to the same server restart, so the retry
  // burst is simultaneous BY CONSTRUCTION — the identical argument as the socket's reconnect.
  return base * (0.75 + Math.random() * 0.5);
};

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Compose the caller's signal with a timeout, WITHOUT `AbortSignal.any` (Safari 16 does not have it) and
 * WITHOUT leaking the timer on the success path — a 10 s timer per request held to completion is a
 * measurable retainer on a page that issues a seed per view per reconnect.
 */
function compose(
  timeoutMs: number,
  external?: AbortSignal,
): { signal: AbortSignal; done: () => void; timedOut: () => boolean } {
  const ctl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctl.abort();
  }, timeoutMs);
  const onAbort = () => ctl.abort();
  if (external) {
    if (external.aborted) ctl.abort();
    else external.addEventListener("abort", onAbort, { once: true });
  }
  return {
    signal: ctl.signal,
    done: () => {
      clearTimeout(timer);
      external?.removeEventListener("abort", onAbort);
    },
    timedOut: () => timedOut,
  };
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════════
// THE CLIENT GATE — `X-Client-Token` on every `/user/v1` request, and on none of `/v1`
// ══════════════════════════════════════════════════════════════════════════════════════════════════════
//
// TWO CREDENTIALS, TWO QUESTIONS. `Authorization: Bearer` says WHICH WALLET; `X-Client-Token` says WHICH APP.
// The trader surface refuses without both when `CLIENT_AUTH_TRADE` is enforcing, and it refuses them with
// DIFFERENT reasons so a client can tell them apart rather than guess:
//
//   401 {code:"client_unauthorized", reason:"absent"}   no X-Client-Token at all
//   401 {code:"client_unauthorized", reason:"expired"}  the token aged out
//   401 {code:"client_unauthorized", reason:"invalid"}  the API KEY was not one we issued (at /client/auth)
//   401 {code:"unauthorized"}                           the client gate PASSED; no user session
//
// That last row is the one worth memorising: `unauthorized` without `client_` means the app authenticated
// fine and the USER did not.
//
// ⚠️ WHY THIS DID NOT EXIST UNTIL 2026-08-20, because the absence looks like an oversight and was not.
// In production this bundle is served from the same origin as the API, and nginx adds the header for
// `/user/v1/` from a pre-minted long-lived token held in a root-only file OUTSIDE the repo. The key was
// deliberately never in the browser. That works perfectly and ONLY there: served from any other origin — a
// `vite dev` server, a preview build, a third-party front end — this client could not authenticate itself at
// all, and the failure reads as `client_unauthorized` with nothing in the app to point at. `CLIENT_API_KEY`
// unset preserves the nginx path byte-for-byte; set, it makes the app self-sufficient.
//
// MINTED ONCE AND CACHED IN MEMORY, NOT IN `localStorage`. The token outlives the page by months, so
// persisting it would save a single request per load — and it would put a credential somewhere every script
// on the origin can read, to save one round trip. In memory it dies with the tab, which is the right
// lifetime for something this app can always re-mint.
//
// CONCURRENT CALLERS SHARE ONE MINT. A cold page issues several `/user/v1` reads at once; without the shared
// promise each would mint its own token and the first request of every session would become N.

/**
 * WHERE THE KEY COMES FROM — a provider, exactly like `setSessionProvider` above and for the same reasons.
 *
 * It defaults to the build-time constant, so a normal build configures nothing. It is a SEAM because a host
 * app may hold the key somewhere this module cannot see — a server-rendered bootstrap, a config fetch — and
 * because a constant read at module load is not something a test can vary.
 */
type ClientKeyProvider = () => string;
let clientKeyProvider: ClientKeyProvider = () => CLIENT_API_KEY;
export function setClientKeyProvider(p: ClientKeyProvider): void {
  clientKeyProvider = p;
  resetClientToken();
}

/** The one route that mints, and the one route that must NOT carry the header it mints. */
const CLIENT_AUTH_PATH = "/client/auth";

/** Re-mint this long before expiry rather than waiting for a 401.
 *
 *  WALL CLOCK, VIA `nowMs()`, AND THAT IS THE SANCTIONED CASE. `lib/time.ts` permits it for exactly two
 *  things, one of which is a CREDENTIAL DEADLINE — a token was issued against wall clock, so comparing its
 *  expiry to CHAIN time would retire it by however far block time is trailing. */
const CLIENT_TOKEN_SKEW_MS = 60_000;

interface MintedToken {
  token: string;
  expiresAt: number;
}

let clientToken: MintedToken | null = null;
let minting: Promise<string | null> | null = null;

/** Test seam: drop the cached token so a case can drive the mint path deterministically. */
export function resetClientToken(): void {
  clientToken = null;
  minting = null;
}

async function mintClientToken(): Promise<string | null> {
  const key = clientKeyProvider();
  if (key.length === 0) return null;
  // The mint goes through `send` rather than a bare `fetch`, so it inherits the timeout, the abort handling
  // and the typed `ApiError` that every other call has. It is a POST and is therefore attempted exactly
  // once, which is correct: a mint that failed is retried by the next request that needs one.
  const res = await send<{ token?: unknown; expiresAt?: unknown }>({
    base: "trade",
    path: CLIENT_AUTH_PATH,
    method: "POST",
    body: { apiKey: key },
    auth: "none",
  });
  const token = typeof res.token === "string" && res.token.length > 0 ? res.token : null;
  if (token === null) return null;
  // A SERVER THAT DOES NOT STATE AN EXPIRY GETS A SHORT ONE, not an infinite one: the failure mode of
  // guessing too long is every request 401ing until the tab is reloaded.
  const expiresAt =
    typeof res.expiresAt === "number" && Number.isFinite(res.expiresAt)
      ? res.expiresAt
      : nowMs() + 5 * 60_000;
  clientToken = { token, expiresAt };
  return token;
}

async function currentClientToken(): Promise<string | null> {
  if (clientKeyProvider().length === 0) return null;
  const cached = clientToken;
  if (cached !== null && cached.expiresAt - CLIENT_TOKEN_SKEW_MS > nowMs()) return cached.token;
  // ONE MINT IN FLIGHT, SHARED. Cleared in a `finally` so a failed mint does not latch a rejected promise
  // that every later caller re-awaits forever.
  minting ??= mintClientToken().finally(() => {
    minting = null;
  });
  return await minting;
}

/** ONE ATTEMPT AT THE WIRE, with the client token already decided by `request`. */
async function send<T>(r: Req, clientTok?: string | null): Promise<T> {
  const method = r.method ?? "GET";
  const base = r.base === "market" ? MARKET_BASE : TRADE_BASE;
  const path = `${base}${r.path}`;
  const url = withParams(path, r.params);
  const timeoutMs = r.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const headers: Record<string, string> = { accept: "application/json" };
  if (r.auth === "required" || r.auth === "optional") {
    const token = sessionProvider();
    if (token === null) {
      // BEFORE the request leaves. Proceeding unauthenticated here sends a money call — the withdraw
      // broadcast is one — with no bearer, and the server's 401 is the first anyone hears of it.
      //
      // `"optional"` deliberately does NOT throw: the route is public, so no session is the NORMAL case and
      // refusing here would put a sign-in prompt back in front of a price. It falls through with no
      // `authorization` header, which is a complete, valid request.
      if (r.auth === "required")
        throw new ApiError({ code: "no_session", message: "not signed in", path });
    } else {
      headers.authorization = `Bearer ${token}`;
    }
  }
  // ── THE JUPITER TRIGGER AUTHORISATION, ON THE ROUTES THAT REACH THE VENDOR ──────────────────────────
  //
  // ⚠️ IT IS A THIRD CREDENTIAL AND IT ANSWERS A THIRD QUESTION. `X-Client-Token` says WHICH APP and a
  // bearer says WHICH WALLET; this says THIS WALLET AUTHORISED US WITH JUPITER, and only Jupiter can issue
  // it. The server holds none, so a request without it is refused `428` rather than served from a store.
  //
  // Keyed on the OWNER the request names, so a wallet switch cannot send the previous wallet's token — the
  // vendor would refuse it and the 401 would read as our fault.
  if (r.triggerOwner !== undefined) {
    const trigger = triggerTokenFor(r.triggerOwner);
    if (trigger !== null) headers["x-trigger-token"] = trigger;
  }
  if (r.body !== undefined) headers["content-type"] = "application/json";
  // ON `/user/v1` ONLY, AND NEVER ON THE ROUTE THAT MINTS IT. Sending it to `/v1` is not merely useless — the
  // read surface has its own gate under a different flag, and a token minted for one is not a credential for
  // the other.
  if (clientTok !== undefined && clientTok !== null && clientTok.length > 0) {
    headers["x-client-token"] = clientTok;
  }

  // A POST is attempted EXACTLY ONCE. This is arithmetic on the retry bound rather than a branch inside the
  // loop, so there is no way to reach a second attempt on a mutating method.
  const maxAttempts = method === "GET" ? MAX_GET_RETRIES + 1 : 1;
  let lastError: ApiError | undefined;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { signal, done, timedOut } = compose(timeoutMs, r.signal);
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        signal,
        ...(r.body !== undefined ? { body: JSON.stringify(r.body) } : {}),
      });
    } catch (cause) {
      const aborted = r.signal?.aborted === true;
      lastError = new ApiError({
        // THREE DISTINCT OUTCOMES, three codes. A caller-cancelled request (a view unmounting, a newer
        // seed superseding this one) must NEVER surface as an error state, and it is indistinguishable
        // from a timeout at the `fetch` boundary unless the two are separated here.
        code: aborted ? "aborted" : timedOut() ? "timeout" : "network",
        message: aborted
          ? "request cancelled"
          : timedOut()
            ? `no response within ${timeoutMs} ms`
            : "could not reach the server",
        path,
      });
      done();
      // A cancelled request is never retried: the caller has already moved on, and retrying it races the
      // thing that replaced it.
      if (aborted) throw lastError;
      if (attempt + 1 < maxAttempts) {
        await sleep(jitter(attempt));
        continue;
      }
      throw lastError;
    }
    done();

    if (res.ok) {
      try {
        return (await res.json()) as T;
      } catch {
        // A 200 whose body is not JSON is a proxy or a build problem, not a server refusal, and it must not
        // be reported as one.
        throw new ApiError({
          code: "malformed",
          message: "the response body was not JSON",
          status: res.status,
          path,
        });
      }
    }

    const retryAfterHeader = Number.parseInt(res.headers.get("retry-after") ?? "", 10);
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    lastError = errorFromBody(
      res.status,
      path,
      body,
      Number.isFinite(retryAfterHeader) ? retryAfterHeader : null,
    );

    // The retry decision reads ONE predicate, `ApiError.transient`, shared with the stitch's seed retry —
    // so "what counts as transient" has one definition. 429 is excluded there and therefore here.
    if (method === "GET" && lastError.transient && attempt + 1 < maxAttempts) {
      await sleep(jitter(attempt));
      continue;
    }
    throw lastError;
  }

  /* c8 ignore next */
  throw lastError ?? new ApiError({ code: "network", message: "request failed", path });
}

/**
 * THE PUBLIC ENTRY POINT — obtains the client token, sends, and re-mints exactly once if the gate says no.
 *
 * ⚠️ THIS RETRIES A POST, WHICH THIS FILE OTHERWISE FORBIDS, AND THE EXCEPTION IS NARROW AND DELIBERATE.
 * The rule at the top ("a POST retry is real money twice") is about a request that REACHED the handler. A
 * `client_unauthorized` is emitted by the gate BEFORE any route runs, so nothing happened: no order, no
 * broadcast, no vendor call, no charge. Retrying it cannot duplicate an effect because there was no effect.
 *
 * The condition is therefore as tight as it can be made: the code must be exactly `client_unauthorized`,
 * which no route emits and only the gate does; we must hold a key, so a deployment behind nginx can never
 * enter this path; and it happens ONCE. Any other 401 — including `unauthorized`, which means the app
 * authenticated and the USER did not — is rethrown untouched.
 */
export async function request<T>(rawRequest: Req): Promise<T> {
  const r = withOwner(rawRequest);
  const needsClientToken = r.base === "trade" && r.path !== CLIENT_AUTH_PATH;
  const tok = needsClientToken ? await currentClientToken() : null;
  try {
    return await send<T>(r, tok);
  } catch (e) {
    const isGate = e instanceof ApiError && e.code === "client_unauthorized";
    if (!isGate || !needsClientToken || clientKeyProvider().length === 0) throw e;
    // The token we hold is not one the server will accept — expired, or minted against a secret that has
    // since rotated. Drop it and mint a fresh one; if THAT is refused the key itself is wrong and the second
    // error is the honest one to surface.
    resetClientToken();
    const fresh = await currentClientToken();
    if (fresh === null) throw e;
    return await send<T>(r, fresh);
  }
}
